import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, LinearProgress, Link, List, ListItem, ListItemText, Stack, Typography } from '@mui/material';
import { cancelExport, fetchExport, fetchExports, isActiveExport, startExport, type EditingAllowance, type ExportRecord, EditingApiError } from '../../services/editingApi';
import { debugLog } from '../../utils/debugLog';
import { formatMinutes, formatMs } from './editStudioModel';
import SendToEditorDialog from './SendToEditorDialog';

interface ExportsPanelProps {
  editId: string;
  editVersion: number;
  /** Unsaved changes: the export would miss them. */
  dirty: boolean;
  outputDurationMs: number;
  resolution: string;
  allowance: EditingAllowance | null;
  onAllowanceChanged: () => void;
  /** For tests: how often a running export is asked about. */
  pollMs?: number;
}

const STATUS_LABEL: Record<ExportRecord['status'], string> = {
  queued: 'Waiting to start',
  preparing: 'Getting ready',
  rendering: 'Rendering',
  uploading: 'Saving the file',
  done: 'Ready',
  failed: 'Didn’t work',
  cancelled: 'Cancelled',
  expired: 'Expired',
};

const STATUS_COLOUR: Record<ExportRecord['status'], 'default' | 'primary' | 'success' | 'error' | 'warning'> = {
  queued: 'default', preparing: 'primary', rendering: 'primary', uploading: 'primary', done: 'success', failed: 'error', cancelled: 'default', expired: 'warning',
};

const formatDate = (iso: string | null): string => (iso ? new Date(iso).toLocaleString() : '');

/** Start an export of this edit, watch it render, download it. */
const ExportsPanel: React.FC<ExportsPanelProps> = ({ editId, editVersion, dirty, outputDurationMs, resolution, allowance, onAllowanceChanged, pollMs = 4000 }) => {
  const [exports, setExports] = useState<ExportRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [problem, setProblem] = useState<{ message: string; code: string | null } | null>(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [sent, setSent] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true; // React 18 development double-mounts: the cleanup below must not stick.
    return () => { mounted.current = false; };
  }, []);

  const load = useCallback(async () => {
    try {
      const list = await fetchExports(editId);
      if (mounted.current) setExports(list);
    } catch (error) {
      debugLog('Exports could not be loaded', error);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [editId]);

  useEffect(() => { load(); }, [load]);

  // Ask about the running ones until they finish.
  useEffect(() => {
    const running = exports.filter((item) => isActiveExport(item.status));
    if (running.length === 0) return undefined;
    const timer = window.setTimeout(async () => {
      const updates = await Promise.all(running.map((item) => fetchExport(item.id).catch(() => null)));
      if (!mounted.current) return;
      const finished = updates.some((update) => update && !isActiveExport(update.status));
      setExports((current) => current.map((item) => updates.find((u) => u && u.id === item.id) || item));
      if (finished) onAllowanceChanged();
    }, pollMs);
    return () => window.clearTimeout(timer);
  }, [exports, onAllowanceChanged, pollMs]);

  const start = async () => {
    setProblem(null);
    setStarting(true);
    try {
      const created = await startExport(editId);
      setExports((current) => [created, ...current]);
      onAllowanceChanged();
    } catch (error) {
      const apiError = error instanceof EditingApiError ? error : new EditingApiError(error);
      setProblem({ message: apiError.message, code: apiError.code });
    } finally {
      setStarting(false);
    }
  };

  const cancel = async (id: string) => {
    try {
      const cancelled = await cancelExport(id);
      setExports((current) => current.map((item) => (item.id === id ? cancelled : item)));
      onAllowanceChanged();
    } catch (error) {
      setProblem({ message: error instanceof Error ? error.message : 'It couldn’t be cancelled.', code: null });
    }
  };

  const minutes = Math.ceil(outputDurationMs / 1000) / 60;
  const left = allowance ? Math.max(0, allowance.exportMinutesPerMonth - allowance.exportMinutesUsed) : null;
  const running = exports.some((item) => isActiveExport(item.status));
  const cannot = allowance && !allowance.enabled ? 'Exports come with Pro and Pro Max.' : allowance && allowance.exportMinutesPerMonth <= 0 ? 'Exports come with Pro and Pro Max. Upgrade in Billing to export.' : null;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Exports render in the cloud and land here as an MP4 you can download for {allowance?.exportRetentionDays ?? 30} days. This one is {formatMs(outputDurationMs)} at {resolution}
        {allowance ? `, about ${formatMinutes(minutes)} of your ${formatMinutes(allowance.exportMinutesPerMonth)} a month (${formatMinutes(left ?? 0)} left).` : '.'}
      </Typography>
      {dirty ? <Alert severity="info" sx={{ mb: 1.5 }}>You have unsaved changes. They’re saved automatically in a moment; the export takes what’s saved.</Alert> : null}
      {cannot ? <Alert severity="info" sx={{ mb: 1.5 }}>{cannot}</Alert> : null}
      {problem ? (
        <Alert severity={problem.code === 'PLAN_UPGRADE_REQUIRED' || problem.code === 'EXPORT_LIMIT' ? 'warning' : 'error'} sx={{ mb: 1.5 }} onClose={() => setProblem(null)}>
          {problem.message}
          {problem.code === 'PLAN_UPGRADE_REQUIRED' ? <> <Link href="/billing">Billing</Link></> : null}
        </Alert>
      ) : null}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button variant="contained" onClick={start} disabled={starting || running || Boolean(cannot)} data-testid="start-export">
          {running ? 'Exporting…' : starting ? 'Starting…' : `Export version ${editVersion}`}
        </Button>
        <Button variant="outlined" onClick={() => setSendOpen(true)} data-testid="send-to-editor">Send to my editor</Button>
      </Stack>
      {sent ? <Alert severity="success" sx={{ mt: 1.5 }} onClose={() => setSent(null)}>{sent}</Alert> : null}
      <SendToEditorDialog
        open={sendOpen}
        editId={editId}
        editVersion={editVersion}
        dirty={dirty}
        onClose={() => setSendOpen(false)}
        onSent={(result) => {
          setSendOpen(false);
          setSent(`Sent to “${result.projectName}”: ${result.tasksAdded} task${result.tasksAdded === 1 ? '' : 's'}${result.tasksReplaced ? ` (replacing ${result.tasksReplaced} from the earlier send)` : ''}. Your editor sees them in the project.`);
        }}
      />
      <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 0.5 }}>Exports of this edit</Typography>
      {loading ? <LinearProgress /> : exports.length === 0 ? (
        <Typography variant="body2" color="text.secondary">None yet.</Typography>
      ) : (
        <List dense disablePadding data-testid="export-list">
          {exports.map((item) => (
            <ListItem key={item.id} disableGutters sx={{ display: 'block', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Chip size="small" label={STATUS_LABEL[item.status]} color={STATUS_COLOUR[item.status]} />
                <ListItemText primary={`Version ${item.editVersion} · ${item.resolution} · ${formatMs(item.outputDurationMs)}`} secondary={formatDate(item.createdAt)} primaryTypographyProps={{ variant: 'body2' }} secondaryTypographyProps={{ variant: 'caption' }} />
                {isActiveExport(item.status) ? <Button size="small" onClick={() => cancel(item.id)}>Cancel</Button> : null}
                {item.status === 'done' && item.downloadUrl ? <Button size="small" variant="outlined" component="a" href={item.downloadUrl} download>Download</Button> : null}
              </Stack>
              {isActiveExport(item.status) ? <LinearProgress variant={item.progress > 0 ? 'determinate' : 'indeterminate'} value={item.progress * 100} sx={{ mt: 0.5 }} /> : null}
              {item.status === 'failed' && item.error ? <Typography variant="caption" color="error">{item.error}</Typography> : null}
              {item.status === 'done' ? (
                <Typography variant="caption" color="text.secondary">
                  {item.sizeBytes ? `${Math.round(item.sizeBytes / 1024 / 1024 * 10) / 10} MB` : ''}{item.expiresAt ? ` · kept until ${formatDate(item.expiresAt)}` : ''}
                  {item.sidecars.map((sidecar) => <React.Fragment key={sidecar.name}> · <Link href={sidecar.url} download>{sidecar.name}</Link></React.Fragment>)}
                </Typography>
              ) : null}
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default ExportsPanel;
